import React, { useRef, useState, useEffect, useImperativeHandle } from 'react'

import { Schedule } from '@/core/api/entities/schedule'
import { cn } from '@/shared/utils/helpers'

import { ScheduleCard } from './ScheduleCard'
import ScheduleSkeleton from './ScheduleSkeleton'

export interface ScheduleCarouselProps {
	schedules: Schedule[]
	onScheduleClick?: (schedule: Schedule) => void
	onFavoriteClick?: (schedule: Schedule) => void
	isLoading?: boolean
	itemsPerPage?: number
	className?: string
	onScrollStateChange?: (
		canScrollLeft: boolean,
		canScrollRight: boolean
	) => void
	showActions?: boolean
	onAssign?: (scheduleId: number, name: string) => void
	onDuplicate?: (scheduleId: number, name: string, description: string) => void
	onEdit?: (scheduleId: number) => void
	onRemove?: (scheduleId: number, name: string, description: string) => void
}

export interface ScheduleCarouselRef {
	scrollLeft: () => void
	scrollRight: () => void
	canScrollLeft: boolean
	canScrollRight: boolean
}

const ScheduleCarousel = React.forwardRef<
	ScheduleCarouselRef,
	ScheduleCarouselProps
>(
	(
		{
			schedules,
			onScheduleClick,
			onFavoriteClick: _onFavoriteClick,
			isLoading = false,
			itemsPerPage = 5,
			className = '',
			onScrollStateChange,
			showActions = true,
			onAssign,
			onDuplicate,
			onEdit,
			onRemove,
		},
		ref
	) => {
		const scrollContainerRef = useRef<HTMLDivElement>(null)
		const itemsRef = useRef<(HTMLDivElement | null)[]>([])
		const [canScrollLeft, setCanScrollLeft] = useState(false)
		const [canScrollRight, setCanScrollRight] = useState(false)

		itemsRef.current = []

		// Expose navigation methods via ref
		useImperativeHandle(
			ref,
			() => ({
				scrollLeft: () => {
					if (scrollContainerRef.current) {
						const scrollAmount = 200
						scrollContainerRef.current.scrollBy({
							left: -scrollAmount,
							behavior: 'smooth',
						})
					}
				},
				scrollRight: () => {
					if (scrollContainerRef.current) {
						const scrollAmount = 200
						scrollContainerRef.current.scrollBy({
							left: scrollAmount,
							behavior: 'smooth',
						})
					}
				},
				canScrollLeft,
				canScrollRight,
			}),
			[canScrollLeft, canScrollRight]
		)

		// Intersection observer for visibility animations
		useEffect(() => {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							entry.target.classList.add('is-visible')
						} else {
							entry.target.classList.remove('is-visible')
						}
					})
				},
				{
					root: scrollContainerRef.current,
					threshold: 0.4,
					rootMargin: '0px -10px 0px -10px',
				}
			)

			itemsRef.current.forEach((item) => {
				if (item) observer.observe(item)
			})

			return () => observer.disconnect()
		}, [schedules])

		// Check scroll position
		useEffect(() => {
			const checkScrollPosition = () => {
				if (!scrollContainerRef.current) return

				const { scrollLeft, scrollWidth, clientWidth } =
					scrollContainerRef.current

				const newCanScrollLeft = scrollLeft > 0
				const newCanScrollRight = scrollLeft < scrollWidth - clientWidth - 10

				setCanScrollLeft(newCanScrollLeft)
				setCanScrollRight(newCanScrollRight)

				if (onScrollStateChange) {
					onScrollStateChange(newCanScrollLeft, newCanScrollRight)
				}
			}

			const container = scrollContainerRef.current
			if (container) {
				container.addEventListener('scroll', checkScrollPosition)
				checkScrollPosition()
			}

			return () => {
				if (container) {
					container.removeEventListener('scroll', checkScrollPosition)
				}
			}
		}, [schedules, onScrollStateChange])

		if (isLoading) {
			return <ScheduleSkeleton count={itemsPerPage} className={className} />
		}

		if (!schedules || schedules.length === 0) {
			return null
		}

		return (
			<div className={cn('relative w-full', className)}>
				<div
					ref={scrollContainerRef}
					className={cn(
						'flex-1 overflow-x-auto overflow-y-hidden py-2 m-0',
						'[&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]',
						'snap-x snap-mandatory scroll-smooth',
						'md:m-0'
					)}
				>
					<div className="flex gap-4 min-w-min">
						{schedules.map((schedule, index) => {
							return (
								<div
									key={schedule.id}
									ref={(el) => {
										if (el) itemsRef.current[index] = el
									}}
									className={cn(
										'shrink-0 flex snap-start w-[165px] md:w-[200px]',
										'transition-all duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
										'opacity-100 scale-100 translate-x-0'
									)}
								>
									<ScheduleCard
										schedule={schedule}
										className="w-full"
										showActions={showActions}
										onClick={onScheduleClick}
										onAssign={onAssign}
										onDuplicate={onDuplicate}
										onEdit={onEdit}
										onRemove={onRemove}
									/>
								</div>
							)
						})}
					</div>
				</div>
			</div>
		)
	}
)

ScheduleCarousel.displayName = 'ScheduleCarousel'

export default ScheduleCarousel
